import {
  type PageBase,
  getCurrentPage,
  getDynamicData,
  getSessionStorage,
  getStaticData,
  isComponentType,
  setSessionStorage,
  getLocalStorageItem,
} from '@avensia/excite';
import anyAscii from 'any-ascii';
import { snakeCase } from 'snake-case';
import { type CartItems } from './events/cart';

const USERID_LOCALSTORAGE_KEY = 'nn_user_id';
type LocalStorageUserData = {
  externalId: string;
};

function isPageApiModel(
  page?: any,
): page is NordicNest.Api.BaseProductListingPageApiModel<Excite.Contentful.CFBasePage | NordicNest.Api.NNCFBasePage> {
  return !!page?.trackingPageType && 'page' in page;
}

function getBrandForPage(page: Excite.ContentApi.IComponent | undefined): string {
  if (isComponentType<NordicNest.Api.BrandPageApiModel>(page)) {
    return page.page.name;
  }
  if (
    isComponentType<NordicNest.Api.CollectionPageApiModel>(page) ||
    isComponentType<NordicNest.Api.ProductPageApiModel>(page)
  ) {
    return page.brand;
  }
  return 'not_available';
}

export function pageToDataLayerInfo(p?: PageBase | undefined) {
  const page = p ?? (getCurrentPage() as PageBase);

  const variantId = new URLSearchParams(window?.location.search).get('variantId');
  return {
    page_name: window?.location?.pathname?.split('/').filter(Boolean).pop() ?? 'not_available',
    page_internal_categorization: 'not_available',
    page_level: 'not_available',
    page_location: window?.location.href,
    page_path: window?.location.pathname,
    item_id: (page as any)?.displaySku ?? (page as any)?.sku ?? variantId ?? undefined,
    page_title: anyAscii(page?.metaInfo?.title ?? document?.title ?? '').toLowerCase(),
    page_type: page?.trackingPageType ?? 'not_available',
    page_brand: getBrandForPage(page),
    ...(isPageApiModel(page) && 'trackingData' in page.page
      ? {
          page_name: page.page.trackingData.internalName,
          page_internal_categorization: (page.page.trackingData.cfTags ?? [])
            .find(x => x.startsWith('pageGroup'))
            ?.replace(/^pageGroup/, ''),
          page_level: (page.page.trackingData.cfTags ?? [])
            .find(x => x.startsWith('pageLevel'))
            ?.replace(/^pageLevel/, ''),
        }
      : {}),
  };
}

export async function cartToDataLayerInfo(order?: NordicNest.Api.OrderTrackingData) {
  const cart = order ?? (await getDynamicData<Excite.Commercetools.CartApiModel>().current());
  if (!cart) {
    return {};
  }

  const { exclVat, inclVat } = cart.subTotal ?? {};
  const selectedShippingMethod = (
    (cart as Excite.Commercetools.CartApiModel)?.viewData ?? (cart as NordicNest.Api.OrderTrackingData)
  )?.selectedShippingMethod;
  const defaultShippingMethod = (cart as Excite.Commercetools.CartApiModel)?.viewData?.defaultShippingMethod;
  const defaultShippingPrice = (cart as Excite.Commercetools.CartApiModel)?.viewData?.defaultShippingPrice;
  return {
    currency: cart?.currency,
    cart_discount: cart?.discountCodes?.map(x => x.viewData?.cartDiscountId)?.filter(Boolean)?.[0],
    cart_discount_key: cart?.discountCodes?.map(x => x.viewData?.cartDiscountKey)?.filter(Boolean)?.[0],
    coupon: cart?.discountCodes?.[0]?.code,
    coupon_value: cart?.discountTotal?.inclVat,
    value: inclVat ?? 0,
    shipping: cart?.shippingCost?.inclVat,
    excl_vat_revenue: exclVat,
    tax: inclVat && exclVat ? Number((inclVat - exclVat).toFixed(2)) : 0,
    selected_shipping_price: cart?.shippingCost?.inclVat,
    selected_shipping_method: selectedShippingMethod,
    default_shipping_method: defaultShippingMethod,
    default_shipping_price: defaultShippingPrice?.inclVat,
  };
}

export function itemPriceToDataLayerInfo(
  price?: Excite.Commercetools.CartPriceApiModel | undefined,
  quantity?: number | undefined,
) {
  if (!price) {
    return {};
  }
  const { exclVat, inclVat } = price?.current ?? {};
  const discount =
    price?.regular?.exclVat && price?.current?.exclVat ? price?.regular?.exclVat - price?.current?.exclVat : 0;
  const pricePerQty = inclVat && quantity ? Number((inclVat / quantity).toFixed(2)) : undefined;

  return {
    item_revenue: inclVat,
    vat: inclVat && exclVat ? Number((inclVat - exclVat).toFixed(2)) : 0,
    discount_percent: exclVat && Number((discount / exclVat).toFixed(2)),
    ...(price ? { price: pricePerQty } : {}),
  };
}

export function cartToEcommerceItems(cart?: Excite.Commercetools.CartApiModel | undefined) {
  const items = cart?.items?.map((product, i) => ({
    ...convertProductToGA4(product.trackingProductInformation ?? {}, {}, i),
    quantity: cart.items.find(item => item.sku === product.sku)?.quantity || 1,
  }));
  return items;
}

export function cartToCartItems(cart?: Excite.Commercetools.CartApiModel | undefined): CartItems {
  if (!cart?.items) return [];
  const cartItems: CartItems = cart.items.map(item => {
    return { itemId: item.sku, quantity: item.quantity };
  });

  return cartItems ?? [];
}

function snakeCaseObjectKeys<T extends object>(obj: T): SnakeCaseKeys<T> {
  const snakeCaseObject: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    snakeCaseObject[snakeCase(key)] = value;
  }
  return snakeCaseObject as SnakeCaseKeys<T>;
}

const ga4ItemListKey = 'ga4-item-list-key';
const ga4List: Record<string, { item_list_id: string; item_list_name: string }> = __BROWSER__
  ? getSessionStorage(ga4ItemListKey) ?? {}
  : {};
export function addListItem(itemid: string, item_list_id: string, item_list_name: string) {
  if (!ga4List[itemid]) {
    ga4List[itemid] = { item_list_id, item_list_name };
  }
  if (__BROWSER__) {
    setSessionStorage(ga4ItemListKey, ga4List);
  }
}

export function getListItem(itemid: string) {
  return ga4List[itemid];
}

type CamelToSnakeCase<S extends string | number | symbol> = S extends `${infer T}${infer U}`
  ? `${T extends Uncapitalize<T> ? '' : '_'}${Lowercase<T>}${CamelToSnakeCase<U>}`
  : S;

type SnakeCaseKeys<T> = {
  [K in keyof T as CamelToSnakeCase<K>]: T[K];
};

export type GA4Product = SnakeCaseKeys<
  Omit<Excite.Commerce.ITrackingProductInformation, 'brand' | 'price' | 'categories' | 'variant' | 'id' | 'name'>
> & {
  item_id: string;
  item_brand: string;
  item_name: string;
  item_variant: string;
  price: number;
  index?: number;
  quantity?: number;
};
export function convertProductToGA4(
  product: Excite.Commerce.TrackingProductInformation,
  additionalProductAttributes?: object,
  index?: number,
): GA4Product {
  const { name, variant, brand, price, categories, id, ...customAttributes } = product;

  const data: GA4Product = {
    item_id: id,
    item_brand: brand,
    item_name: name,
    item_variant: variant,
    price,
    index,
    ...snakeCaseObjectKeys(customAttributes),
    ...(additionalProductAttributes ?? {}),
    ...addGA4Categories(categories),
    ...getListItem(id),
  };

  return data;
}

export function addGA4Categories(categories: string[]) {
  const obj: Record<string, any> = {};
  categories?.forEach((c, i) => {
    const suffix = i > 0 ? i + 1 : '';
    obj['item_category' + suffix] = c;
  });
  return obj;
}

export function isPackageLineItem(
  lineItem: Excite.Commercetools.LineItemApiModel,
): lineItem is Excite.Commercetools.PackageLineItemApiModel {
  return 'includedInPackage' in lineItem;
}

export async function getLineItemTrackingInfo() {
  const cart = await getDynamicData<Excite.Commercetools.CartApiModel>().current();

  const items = cart.items
    .flatMap(l => (isPackageLineItem(l) ? l.includedInPackage : l))
    .map((l, i) => convertProductToGA4(l.trackingProductInformation, { quantity: l.quantity }, i));
  return { items, cart };
}

export function getCheckoutPageUrl(): string {
  const wellKnownPages = getStaticData<NordicNest.Api.WellknownPagesApiModel>();
  return window.location.protocol + '//' + window.location.host + wellKnownPages?.checkoutPageUrl;
}

export function getRetailMediaSessionId(): string | undefined {
  if (!__BROWSER__) return undefined;

  let sessionId = sessionStorage.getItem('retail_media_session_id');

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('retail_media_session_id', sessionId);
  }

  return sessionId;
}

export function getTrackingTimestamp(): string {
  return new Date().toISOString().split('Z')[0];
}

export function getUserId(): string | undefined {
  if (!__BROWSER__) return undefined;

  const storedData = getLocalStorageItem<string>(USERID_LOCALSTORAGE_KEY);
  let cachedData: LocalStorageUserData | undefined;
  if (storedData) {
    try {
      cachedData = JSON.parse(storedData);
      return cachedData?.externalId;
    } catch {
      // Failed to parse cached user data
    }
  }
  return undefined;
}
